import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { apiErrorMessage } from '@shared/api/client';
import { uploadDocument } from '@shared/api/documents';
import { cn } from '@/lib/cn';
import type { DocumentKind, Uuid } from '@shared/types/api';

// Upload dialog for associates. Picks DOC kind (SUPPORTING / DRAFT / FINAL /
// REFERENCE) and performs the two-phase upload. Uploading a FINAL is a
// one-click flow; uploading a DRAFT offers a "then send for approval"
// shortcut which callers can wire to /transactions/:id/send-for-approval.

type Props = {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
  defaultKind?: DocumentKind;
  // Restricts the kind picker — callers derive this from the tx status so
  // the associate can't pick a kind the server would reject (e.g. DRAFT
  // once the tx is APPROVED). When omitted, all kinds are shown.
  availableKinds?: DocumentKind[];
  onUploaded?: (docId: Uuid, kind: DocumentKind) => void;
};

const KIND_LABELS: Record<DocumentKind, { title: string; hint: string }> = {
  SUPPORTING: {
    title: 'Supporting document',
    hint: 'Evidence or reference material for the client.',
  },
  DRAFT: {
    title: 'Draft',
    hint: 'Work-in-progress version (auto-versioned).',
  },
  FINAL: {
    title: 'Final document',
    hint: 'Closes the draft cycle and opens the support window.',
  },
  REFERENCE: {
    title: 'Reference',
    hint: 'Ancillary file (receipts, corrections, etc.).',
  },
};

export function UploadDocumentDialog({
  open,
  onClose,
  transactionId,
  defaultKind = 'DRAFT',
  availableKinds,
  onUploaded,
}: Props) {
  // Show every kind when no explicit restriction was passed — keeps the
  // admin / edge-case callers working. Parent associate view always
  // passes a trimmed list.
  const kinds: DocumentKind[] =
    availableKinds && availableKinds.length > 0
      ? availableKinds
      : (Object.keys(KIND_LABELS) as DocumentKind[]);
  const initialKind = kinds.includes(defaultKind) ? defaultKind : kinds[0];
  const [kind, setKind] = useState<DocumentKind>(initialKind);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const upload = useMutation({
    mutationFn: (f: File) => uploadDocument(transactionId, f, kind),
    onSuccess: (doc) => {
      // A FINAL upload auto-advances the tx server-side (APPROVED →
      // FINAL_DOC_READY); a DRAFT may advance IN_PROGRESS → REVIEW. Either
      // way the list row's status is stale, so invalidate both the per-tx
      // detail key and the list key that drives the associate's table.
      qc.invalidateQueries({ queryKey: ['transaction', transactionId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['chat', transactionId, 'history'] });
      onUploaded?.(doc.id, kind);
      onClose();
      setFile(null);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (upload.isPending) return;
        onClose();
        setFile(null);
      }}
      title="Upload document"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button disabled={!file || upload.isPending} onClick={() => file && upload.mutate(file)}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium text-gray-700">Kind</div>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={cn(
                  'rounded-md border p-2 text-left text-xs',
                  kind === k
                    ? 'border-brand-600 bg-brand-50/40'
                    : 'border-gray-200 bg-white hover:bg-gray-50',
                )}
              >
                <div className="font-medium text-gray-900">{KIND_LABELS[k].title}</div>
                <div className="text-gray-500">{KIND_LABELS[k].hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setFile(f);
          }}
          onClick={() => fileRef.current?.click()}
          className={cn(
            'flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center',
            dragOver ? 'border-brand-600 bg-brand-50/40' : 'border-gray-300 bg-gray-50',
          )}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#15803d"
            strokeWidth="1.75"
            aria-hidden
          >
            <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <polyline points="14 3 14 9 20 9" />
            <path d="M12 18v-6" />
            <polyline points="9 15 12 12 15 15" />
          </svg>
          {file ? (
            <>
              <div className="mt-2 text-sm font-medium text-gray-900">{file.name}</div>
              <div className="text-xs text-gray-500">
                {(file.size / 1024).toFixed(0)} KB · Click to replace
              </div>
            </>
          ) : (
            <>
              <div className="mt-2 text-sm font-medium text-gray-900">
                Drop a file here or click to browse
              </div>
              <div className="text-xs text-gray-500">
                PDF, DOCX, XLSX, images, ZIP — up to 50 MB
              </div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setFile(f);
              e.target.value = '';
            }}
          />
        </div>

        {upload.isError ? (
          <p className="text-sm text-red-600">
            {apiErrorMessage(upload.error, (upload.error as Error)?.message)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
